import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Plane, Calendar } from "lucide-react";
import usePageMeta from "../../hooks/usePageMeta";
import useJsonLd from "../../hooks/useJsonLd";
import { api, track } from "../../lib/api";
import { SearchBox, StickySearchCTA } from "../../components/b2c/SearchBox";
import { AirlineCard, RouteCard, SectionHeading } from "../../components/b2c/Blocks";

const IMG_FALLBACK = "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

export default function SeasonPage() {
  const { slug } = useParams();
  const [season, setSeason] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [airlines, setAirlines] = useState([]);
  const [routes, setRoutes] = useState([]);

  useEffect(() => {
    api.get(`/seasons/${slug}`).then((r) => setSeason(r.data)).catch(() => setNotFound(true));
    api.get("/airlines").then((r) => setAirlines(r.data)).catch(() => {});
    api.get("/routes").then((r) => setRoutes(r.data)).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (season) track("season_view", `/flights/season/${slug}`, { title: season.title });
  }, [season, slug]);

  usePageMeta(season?.seo_title || season?.title, season?.seo_description);

  const featuredAirlines = useMemo(
    () => airlines.filter((a) => (season?.airlines || []).includes(a.slug)),
    [airlines, season]
  );
  const featuredRoutes = useMemo(
    () => routes.filter((r) => (season?.routes || []).includes(r.slug)),
    [routes, season]
  );

  const jsonLd = useMemo(() => {
    if (!season) return null;
    const origin = typeof window !== "undefined" ? window.location.origin : "https://sellvago.com";
    const url = `${origin}/flights/season/${slug}`;
    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Flights", "item": `${origin}/flights` },
            { "@type": "ListItem", "position": 2, "name": "Seasons", "item": `${origin}/flights/deals` },
            { "@type": "ListItem", "position": 3, "name": season.title, "item": url },
          ],
        },
        {
          "@type": "Event",
          "name": season.title,
          "url": url,
          "description": season.hero_subtext,
          "startDate": season.start_at || undefined,
          "endDate": season.end_at || undefined,
          "eventStatus": "https://schema.org/EventScheduled",
          "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
          "organizer": { "@type": "Organization", "name": "Sellvago Farevault", "url": `${origin}/flights` },
          "image": season.banner_image_url || undefined,
        },
      ],
    };
  }, [season, slug]);

  useJsonLd("season-page", jsonLd);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="season-not-found">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Season not found</h1>
        <p className="mt-3 text-slate-500 text-sm">{"The seasonal deal you're looking for isn't live right now."}</p>
        <Link to="/flights" className="mt-6 inline-flex items-center gap-2 text-[#970C10] font-bold text-sm">
          Back to Farevault <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  if (!season) {
    return <div className="min-h-[400px] flex items-center justify-center text-slate-400 text-sm">Loading…</div>;
  }

  const heroImg = season.banner_image_url || IMG_FALLBACK;

  return (
    <div data-testid="season-page">
      <section className="relative">
        <div className="absolute inset-0 h-[420px] sm:h-[380px]">
          <img src={heroImg} alt={season.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-900/55" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-20 pb-8">
          <div className="max-w-2xl fade-up">
            <p className="text-xs uppercase tracking-[0.2em] text-[#E8767A] font-bold mb-3">Seasonal Deal</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter text-white leading-[1.05]">
              {season.hero_headline || season.title}
            </h1>
            {season.hero_subtext && (
              <p className="mt-4 text-white/85 text-base md:text-lg max-w-lg">{season.hero_subtext}</p>
            )}
            {(season.start_at || season.end_at) && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1.5 rounded-full text-xs text-white font-semibold">
                  <Calendar className="w-3.5 h-3.5" />
                  {fmtDate(season.start_at)} {season.end_at ? `— ${fmtDate(season.end_at)}` : ""}
                </span>
                {(() => {
                  if (!season.end_at) return null;
                  const days = Math.ceil((new Date(season.end_at).getTime() - Date.now()) / 86400000);
                  if (days <= 0 || days > 21) return null;
                  return (
                    <span className="inline-flex items-center gap-1.5 bg-[#00E5FF] text-black px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide" data-testid="season-countdown-chip">
                      <span className="w-1.5 h-1.5 rounded-full bg-black fv-tick" />
                      Ends in {days} day{days > 1 ? "s" : ""}
                    </span>
                  );
                })()}
              </div>
            )}
          </div>
          <div className="mt-8">
            <SearchBox
              page={`/flights/season/${slug}`}
              defaultDep={season.default_dep || "KHI"}
              defaultRet={season.default_ret || "DXB"}
            />
          </div>
        </div>
      </section>

      {featuredAirlines.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-20">
          <SectionHeading overline="Featured airlines" title="Best deals from these carriers" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="season-airlines-grid">
            {featuredAirlines.map((a) => <AirlineCard key={a.slug} airline={a} />)}
          </div>
        </section>
      )}

      {featuredRoutes.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-20">
          <SectionHeading overline="Curated routes" title="Popular routes this season" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="season-routes-grid">
            {featuredRoutes.map((r) => <RouteCard key={r.slug} route={r} />)}
          </div>
        </section>
      )}

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-20">
        <Link
          to="/flights/deals"
          className="inline-flex items-center gap-2 text-slate-500 hover:text-[#970C10] transition-colors duration-200 text-sm font-semibold"
          data-testid="season-back-to-deals"
        >
          <Plane className="w-4 h-4" /> See all seasonal deals
        </Link>
      </section>

      <StickySearchCTA />
    </div>
  );
}
